import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import MUITable from "./MUITable";
import { GridColDef } from "@mui/x-data-grid";
import "./Styles.css";
import Chatbot from "../components/ChatBot/ChatBot";
import {
  deleteKnowledgeAssets,
  downloadAsset,
  getKnowledgeAssetsApi,
  interactKnowledgeAsset,
  previewCobol,
  previewKnowledgeAsset,
  previewSas,
} from "../services/BackendServices";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import { HTMLComponent } from "../components/HTMLComponent";
import { SnackbarOrigin } from "@mui/material/Snackbar";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDispatch } from "react-redux";
import { useAppSelector } from "../redux/Hook";
import CreateKnowledge from "./CreateKnowledge";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@emotion/react";
import useCustomStyles from "../hooks/CustomStylesHook";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  DATABASE,
  ERROR_DELETION,
  ERROR_DOWNLOAD_ASSET,
  ERROR_GENERATING_RESPONSE,
} from "../constants/constants";

interface asset {
  asset_id: string;
  asset_name: string;
  createdOn: string;
  type: string;
}

const initialValue = {
  asset_name: "",
  createdOn: "",
  type: "",
  asset_id: "",
};

interface State extends SnackbarOrigin {
  open: boolean;
}

const styles = (theme: any) => ({
  browseDialog: {
    width: "100%",
  },
});

const KnowledgeAssets: React.FC = () => {
  const notification = useAppSelector((state) => state.NotificationAlert);
  const theme = useTheme();
  const classes = useCustomStyles(styles, theme);

  const dispatch = useDispatch();
  const [openChat, setOpenChat] = useState(false);
  const [openIframe, setOpenIframe] = useState(false);
  const [blobUrl, setBlobUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [assets, setAssets] = useState<asset[]>([]);
  const [selectedAsset, setSelectedAsset] = useState<asset>(initialValue);
  const [dialodOpen, setDialogOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [isUserUpdate, setIsUserUpdate] = useState<boolean>(false);

  useEffect(() => {
    getKnowLedgeAssets();
  }, []);

  useEffect(() => {
    if (isUserUpdate) {
      getKnowLedgeAssets();
    }
  }, [isUserUpdate]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, row: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleDelete = () => {
    if (selectedRow) {
      handleDeleteAsset(selectedRow?.asset_id);
    }
    handleMenuClose();
  };

  const handlePreviewOption = () => {
    if (selectedRow) {
      generateResponse(selectedRow);
    }
    handleMenuClose();
  };

  const handleInteractOption = () => {
    if (selectedRow) {
      handleInteract(selectedRow);
    }
    handleMenuClose();
  };

  const handleDownloadAsset = async (params: any) => {
    if (params?.type === DATABASE) {
      return;
    }

    try {
      const response = await downloadAsset(params?.asset_id);
      if (response?.success && response?.data) {
        const blob = new Blob([response?.data], { type: "text/plain" });
        HTMLComponent(params?.asset_name, blob);
      } else {
        dispatch({
          type: "SEND_ALERT",
          data: {
            ...notification,
            enable: true,
            type: "error",
            message: response?.data?.message ?? response?.message,
            duration: 3000,
          },
        });
      }
    } catch (error) {
      console.error(ERROR_DOWNLOAD_ASSET, error);
    }
  };

  const columns: GridColDef<asset>[] = [
    {
      field: "asset_name",
      headerName: "Discovery Asset",
      minWidth: 250,
      editable: true,
      flex: 1,
      renderCell: (params: any) => {
        const isDatabaseType = params?.row?.type === DATABASE;

        return (
          <div
            className={
              isDatabaseType
                ? "asset-name-cursor-black"
                : "asset-name-cursor-blue"
            }
            onClick={() => !isDatabaseType && handleDownloadAsset(params?.row)}
          >
            {params?.row?.asset_name}
          </div>
        );
      },
    },
    {
      field: "project_name",
      headerName: "Project",
      minWidth: 250,
      editable: true,
      flex: 1,
    },
    {
      field: "type",
      headerName: "Type",
      minWidth: 160,
      editable: true,
      flex: 1,
    },
    {
      field: "creation_date",
      headerName: "Creation Date",
      type: "string",
      minWidth: 250,
      editable: true,
      flex: 1,
    },
    {
      field: "actions",
      headerName: "Actions",
      type: "string",
      minWidth: 100,
      editable: true,
      flex: 1,
      renderCell: (params: any) => (
        <div key={params?.row.asset_id}>
          <IconButton
            color="primary"
            size="small"
            onClick={(event) => handleMenuClick(event, params?.row)}
            aria-label="more"
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={
              Boolean(anchorEl) &&
              selectedRow?.asset_id === params?.row?.asset_id
            }
            onClose={handleMenuClose}
          >
            <MenuItem onClick={handlePreviewOption}>
              <RemoveRedEyeIcon
                fontSize="small"
                className="knowledge-assets-actions"
              />
              Preview
            </MenuItem>
            <MenuItem onClick={handleInteractOption}>
              <SmartToyIcon
                fontSize="small"
                className="knowledge-assets-actions"
              />
              Interact
            </MenuItem>
            <MenuItem onClick={handleDelete}>
              <DeleteIcon
                fontSize="small"
                className="knowledge-assets-actions"
              />
              Delete
            </MenuItem>
          </Menu>
        </div>
      ),
    },
  ];
  const handleInteract = (params: any) => {
    setSelectedAsset(params);
    setOpenChat(!openChat);
  };

  const handlePreview = (params: any) => {
    setSelectedAsset(params);
    generateResponse(params);
  };

  const handleDeleteAsset = async (asset_id: string) => {
    const result = await deleteKnowledgeAssets(asset_id);
    if (result?.success) {
      await getKnowLedgeAssets();
    } else {
      console.error(ERROR_DELETION, result?.message);
    }
  };

  const getKnowLedgeAssets = async () => {
    setLoading(true);
    const response: any = await getKnowledgeAssetsApi();

    if (response && response?.data) {
      const updatedItems =
        response?.data?.mongo_knowledge_assets?.filter(
          (item: any) => item?.type !== "markdown"
        ) || [];

      if (updatedItems?.length > 0) {
        const sortedData = updatedItems
          ?.map((item: any, index: number) => ({
            ...item,
            id: index + 1,
          }))
          ?.sort((a: any, b: any) => {
            const dateA: any = new Date(a?.creation_date);
            const dateB: any = new Date(b?.creation_date);
            return dateB - dateA;
          });
        setAssets(sortedData);
      } else {
        setAssets([]);
      }
    } else {
      dispatch({
        type: "SEND_ALERT",
        data: {
          ...notification,
          enable: true,
          type: "error",
          message: response?.data?.message ?? response?.message,
          duration: 3000,
        },
      });
    }
    setLoading(false);
  };

  const closeChatBot = () => {
    setSelectedAsset(initialValue);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };
  const generateResponse = async (params: any) => {
    setLoading(true);
    try {
      setSelectedAsset(params);
      const body = {
        user_input: "details",
        selected_asset: params?.asset_id,
      };

      const response: any = await previewKnowledgeAsset(params?.asset_id);

      HTMLComponent(params?.asset_name, response?.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error(ERROR_GENERATING_RESPONSE, error);
    }
  };

  return (
    <>
      <Dialog
        open={dialodOpen}
        onClose={handleCloseDialog}
        className={classes?.browseDialog}
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h5" gutterBottom>
              Browse Project
            </Typography>
            <Button onClick={handleCloseDialog}>
              <CloseIcon />
            </Button>
          </Box>
        </DialogTitle>

        <DialogContent>
          <CreateKnowledge getKnowLedgeAssets={getKnowLedgeAssets} />
        </DialogContent>
      </Dialog>
      <Box display={"flex"} flexDirection={"column"} gap={2}>
        <Box display={"flex"} justifyContent={"right"}>
          <Button
            onClick={() => setDialogOpen(true)}
            color={"primary"}
            variant="contained"
          >
            Add
            <AddIcon />
          </Button>
        </Box>
        <div className="knowledge-table-container">
          <div>
            <MUITable
              loading={loading}
              columns={columns}
              rows={assets}
              tableHeight={"80vh"}
              defaultPageSize={10}
            />
          </div>
        </div>
      </Box>

      <div className="knowledgeAsset-chatbot-container">
        <Chatbot
          closeChatBot={closeChatBot}
          open={openChat}
          selectedAsset={selectedAsset?.asset_id}
        />
      </div>
      {openIframe ? (
        <iframe
          width="100%"
          height="100%"
          src={blobUrl}
          frameBorder="0"
        ></iframe>
      ) : null}
    </>
  );
};

export default KnowledgeAssets;
